import { Button } from '@/components/Button';
import { LineSeparator } from '@/components/LineSeparator';
import { Switch } from '@/components/Switch';
import { Text } from '@/components/Text';
import { TextArea } from '@/components/TextArea';
import { TextInput } from '@/components/TextInput';
import { useSubscription } from '@/hooks/useSubscription';
import { useCardStore } from '@/stores/useCardStore';
import { CardLeadCapture as CardLeadCaptureType } from '@/types/card';
import { handleError } from '@/utils/handleError';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

export const UpdateCardLeadCapture = () => {
  const { t } = useTranslation();
  const { card, updateCard } = useCardStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [leadCapture, setLeadCapture] = useState<CardLeadCaptureType | null>(
    null,
  );
  const { hasActiveSubscription } = useSubscription(card?.user ?? null);

  useEffect(() => {
    if (card?.lead_capture) {
      setLeadCapture(card.lead_capture);
    }
  }, [card]);

  // Schéma de validation Yup
  const validationSchema = yup.object().shape({
    title: yup.string().required(t('common:validation.required')),
    description: yup.string().required(t('common:validation.required')),
    button_text: yup.string().required(t('common:validation.required')),
    disclaimer: yup.string().required(t('common:validation.required')),
  });

  const handleSwitchChange = async (checked: boolean) => {
    if (!leadCapture || !card) return;
    try {
      setLoading(true);
      const updatedLeadCapture = {
        ...leadCapture,
        is_active: checked,
      };
      await updateCard(card.slug, {
        ...card,
        lead_capture: updatedLeadCapture,
      });
      setLeadCapture(updatedLeadCapture);
      toast.success(
        checked
          ? t('updateCard:lead-capture.activated')
          : t('updateCard:lead-capture.deactivated'),
      );
    } catch (error) {
      handleError(error, undefined, navigate);
    } finally {
      setLoading(false);
    }
  };

  if (!leadCapture) return null;

  return (
    <div className="space-y-6">
      {/* Activer/désactiver */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <Text className="font-medium text-gray-900">
            {t('updateCard:lead-capture.activate')}
          </Text>
          <Text className="text-sm text-gray-500">
            {t('updateCard:lead-capture.activate-description')}
          </Text>
        </div>
        <Switch
          checked={leadCapture.is_active}
          onChange={handleSwitchChange}
          disabled={loading}
        />
      </div>

      <LineSeparator />

      {/* Formulaire avec Formik */}
      <Formik
        enableReinitialize
        initialValues={{
          title: leadCapture.title || '',
          description: leadCapture.description || '',
          button_text: leadCapture.button_text || '',
          disclaimer: leadCapture.disclaimer || '',
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          if (!card) return;
          try {
            setFormLoading(true);
            await updateCard(card.slug, {
              ...card,
              lead_capture: {
                ...leadCapture,
                ...values,
              },
            });
            setLeadCapture({ ...leadCapture, ...values });
            toast.success(t('updateCard:lead-capture.saved'));
          } catch (error) {
            handleError(error);
          } finally {
            setFormLoading(false);
            setSubmitting(false);
          }
        }}
      >
        {({ errors, touched }) => (
          <Form className="space-y-6">
            <Text className="font-medium text-gray-900">
              {t('updateCard:lead-capture.form-settings')}
            </Text>
            <div className="space-y-4">
              <TextInput
                label={t('updateCard:lead-capture.form-title')}
                name="title"
                type="text"
                useFormik={true}
                isRequired
                placeholder={t(
                  'updateCard:lead-capture.form-title-placeholder',
                )}
                disabled={formLoading}
                error={!!touched.title && !!errors.title && errors.title}
              />
              <TextArea
                label={t('updateCard:lead-capture.form-description')}
                name="description"
                placeholder={t(
                  'updateCard:lead-capture.form-description-placeholder',
                )}
                useFormik={true}
                isRequired
                rows={4}
                disabled={formLoading}
                error={
                  !!touched.description &&
                  !!errors.description &&
                  errors.description
                }
              />
              <TextInput
                label={t('updateCard:lead-capture.button-text')}
                name="button_text"
                type="text"
                useFormik={true}
                isRequired
                placeholder={t(
                  'updateCard:lead-capture.button-text-placeholder',
                )}
                disabled={formLoading}
                error={
                  !!touched.button_text &&
                  !!errors.button_text &&
                  errors.button_text
                }
              />
              <div className="relative flex flex-col space-y-2">
                <TextArea
                  label={t('updateCard:lead-capture.disclaimer')}
                  name="disclaimer"
                  rows={4}
                  useFormik={true}
                  isRequired
                  placeholder={t(
                    'updateCard:lead-capture.disclaimer-placeholder',
                  )}
                  disabled={formLoading}
                  error={
                    !!touched.disclaimer &&
                    !!errors.disclaimer &&
                    errors.disclaimer
                  }
                />
              </div>
            </div>
            {/* Bouton de sauvegarde */}
            <div>
              <Button
                type="submit"
                disabled={formLoading || !hasActiveSubscription()}
                className="w-full"
              >
                {formLoading ? t('common:saving') : t('common:save')}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
